import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiDownload,
  FiLock,
  FiEdit2,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import { getLocale, t, tn, useI18n } from '../../i18n';
import { payrollService } from '../../services/payrollService';
import { timesheetService } from '../../services/timesheetService';
import {
  buildTimesheetRegisterCsv,
  buildTimesheetRegisterFilename,
} from '../../services/payroll/exportProfiles';
import type {
  PayPeriod,
  PayPeriodStatus,
  PayrollAggregateRow,
  Timesheet,
  TimesheetAuditEvent,
  TimesheetEntry,
  TimesheetEntryType,
} from '../../services/payroll/types';
import PayCollapsible from './PayCollapsible';
import './PayrollWorkspace.css';

type GroupBy = 'employee' | 'site' | 'pay_band' | 'cost_centre' | 'entry_type';

/** Which slice of an opened timesheet is on screen. */
type DrillTab = 'lines' | 'adjustments' | 'audit';

type ReviewFilter = 'all' | 'pending' | 'flagged' | 'approved';

const REVIEW_FILTERS: { key: ReviewFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Awaiting decision' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'approved', label: 'Approved' },
];

const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
  { key: 'employee', label: 'Employee' },
  { key: 'site', label: 'Site' },
  { key: 'pay_band', label: 'Pay band' },
  { key: 'cost_centre', label: 'Cost centre' },
  { key: 'entry_type', label: 'Entry type' },
];

const MANUAL_ENTRY_TYPES: TimesheetEntryType[] = [
  'training', 'standby', 'call_out', 'bonus', 'expense', 'deduction', 'adjustment', 'leave',
];

const ENTRY_TYPE_LABELS: Record<string, string> = {
  shift: 'Shift',
  leave: 'Leave',
  training: 'Training',
  standby: 'Standby',
  call_out: 'Call-out',
  bonus: 'Bonus',
  expense: 'Expense',
  deduction: 'Deduction',
  adjustment: 'Adjustment',
};

/* Statuses reach the UI as raw enum values; these give them English labels
   without disturbing the lowercase keys the audit trail already translates. */
const PERIOD_STATUS_LABELS: Record<string, string> = {
  open: 'Open||pay period',
  in_review: 'In review||pay period',
  approved: 'Approved||pay period',
  locked: 'Locked||pay period',
  paid: 'Paid||pay period',
};

const TIMESHEET_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft||timesheet',
  submitted: 'Submitted||timesheet',
  approved: 'Approved||timesheet',
  rejected: 'Rejected||timesheet',
  locked: 'Locked||timesheet',
};

const STATUS_CHIP: Record<string, string> = {
  draft: 'pay-chip--muted',
  submitted: 'pay-chip--warn',
  approved: 'pay-chip--ok',
  rejected: 'pay-chip--error',
  locked: 'pay-chip--band',
  open: 'pay-chip--muted',
  in_review: 'pay-chip--warn',
  paid: 'pay-chip--ok',
};

/** Time-based manual entries are entered in hours; the rest in units. */
const isTimeBased = (type: TimesheetEntryType): boolean => (
  ['training', 'standby', 'call_out', 'leave', 'adjustment'].includes(type)
);

function todayKey(): string {
  const now = new Date();
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function normaliseCurrencyCode(value: string | null | undefined): string {
  const candidate = value?.trim().toUpperCase() ?? '';
  return /^[A-Z]{3}$/.test(candidate) ? candidate : 'EUR';
}

function formatMoney(value: number, currencyCode: string): string {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatHours(minutes: number): string {
  return (minutes / 60).toLocaleString(getLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatTimestamp(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(getLocale(), {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatTimeWindow(start: string | null, end: string | null, timeZone: string): string {
  const formatTime = (value: string | null): string | null => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
  };

  const startLabel = formatTime(start);
  const endLabel = formatTime(end);
  if (!startLabel && !endLabel) return '-';
  return `${startLabel ?? '?'} - ${endLabel ?? '?'}`;
}

interface TimesheetsPageProps {
  /** Locking a period is a commercial control reserved for company admins. */
  canLockPeriod?: boolean;
}

export default function TimesheetsPage({ canLockPeriod = false }: TimesheetsPageProps) {
  useI18n();

  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [loadedTimesheetPeriodId, setLoadedTimesheetPeriodId] = useState<string | null>(null);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [auditEvents, setAuditEvents] = useState<TimesheetAuditEvent[]>([]);
  const [aggregate, setAggregate] = useState<PayrollAggregateRow[]>([]);
  const [groupBy, setGroupBy] = useState<GroupBy>('employee');
  const [companyCurrencyCode, setCompanyCurrencyCode] = useState('EUR');

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTimesheets, setIsLoadingTimesheets] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [periodLabel, setPeriodLabel] = useState('');
  const [periodStart, setPeriodStart] = useState(todayKey());
  const [periodEnd, setPeriodEnd] = useState(todayKey());
  const [periodPayDate, setPeriodPayDate] = useState(todayKey());
  const [selectedPayDate, setSelectedPayDate] = useState('');
  const [showLockConfirmation, setShowLockConfirmation] = useState(false);
  const [showPaidConfirmation, setShowPaidConfirmation] = useState(false);
  const [approvingTimesheet, setApprovingTimesheet] = useState<Timesheet | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [rejectingTimesheet, setRejectingTimesheet] = useState<Timesheet | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [removingEntry, setRemovingEntry] = useState<TimesheetEntry | null>(null);

  const [drillTab, setDrillTab] = useState<DrillTab>('lines');
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingPayDate, setIsEditingPayDate] = useState(false);

  const [entryType, setEntryType] = useState<TimesheetEntryType>('training');
  const [entryDate, setEntryDate] = useState(todayKey());
  const [entryDescription, setEntryDescription] = useState('');
  const [entryQuantity, setEntryQuantity] = useState('1');
  const [entryRate, setEntryRate] = useState('0');
  const selectedPeriodIdRef = useRef<string | null>(null);
  const expandedIdRef = useRef<string | null>(null);
  const timesheetLoadSequence = useRef(0);
  const detailLoadSequence = useRef(0);

  const clearExpandedDetail = useCallback(() => {
    detailLoadSequence.current += 1;
    expandedIdRef.current = null;
    setExpandedId(null);
    setEntries([]);
    setAuditEvents([]);
    setRemovingEntry(null);
    setDrillTab('lines');
  }, []);

  const selectPayPeriod = useCallback((payPeriodId: string | null, defaultEntryDate?: string) => {
    if (payPeriodId === selectedPeriodIdRef.current) return;

    selectedPeriodIdRef.current = payPeriodId;
    timesheetLoadSequence.current += 1;
    setSelectedPeriodId(payPeriodId);
    setLoadedTimesheetPeriodId(null);
    setTimesheets([]);
    setAggregate([]);
    setIsLoadingTimesheets(Boolean(payPeriodId));
    clearExpandedDetail();
    setApprovingTimesheet(null);
    setApprovalNote('');
    setRejectingTimesheet(null);
    setShowLockConfirmation(false);
    setShowPaidConfirmation(false);
    setIsEditingPayDate(false);
    setReviewFilter('all');
    setSearchTerm('');
    if (defaultEntryDate) setEntryDate(defaultEntryDate);
  }, [clearExpandedDetail]);

  const selectedPeriod = useMemo(
    () => periods.find((period) => period.id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId],
  );
  const selectedCurrencyCode = normaliseCurrencyCode(
    selectedPeriod?.calculationCurrencyCode ?? companyCurrencyCode,
  );

  const isPeriodLocked = selectedPeriod?.status === 'locked' || selectedPeriod?.status === 'paid';
  const canEditPeriod = selectedPeriod?.status === 'open' || selectedPeriod?.status === 'in_review';
  const hasLoadedSelectedPeriod = Boolean(
    selectedPeriodId
    && selectedPeriodId === selectedPeriodIdRef.current
    && loadedTimesheetPeriodId === selectedPeriodId,
  );
  const displayedTimesheets = useMemo(
    () => (hasLoadedSelectedPeriod ? timesheets : []),
    [hasLoadedSelectedPeriod, timesheets],
  );
  const displayedAggregate = useMemo(
    () => (hasLoadedSelectedPeriod ? aggregate : []),
    [aggregate, hasLoadedSelectedPeriod],
  );

  const requireLoadedPeriod = (): PayPeriod => {
    const activeId = selectedPeriodIdRef.current;
    if (
      !activeId
      || loadedTimesheetPeriodId !== activeId
      || !selectedPeriod
      || selectedPeriod.id !== activeId
    ) {
      throw new Error('Wait for the selected pay period to finish loading.');
    }
    return selectedPeriod;
  };

  const requireLoadedTimesheet = (timesheetId: string): { period: PayPeriod; timesheet: Timesheet } => {
    const period = requireLoadedPeriod();
    const timesheet = displayedTimesheets.find((candidate) => (
      candidate.id === timesheetId && candidate.payPeriodId === period.id
    ));
    if (!timesheet) throw new Error('That timesheet does not belong to the loaded pay period.');
    return { period, timesheet };
  };

  const loadPeriods = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [next, settings] = await Promise.all([
        timesheetService.listPayPeriods(),
        payrollService.fetchPayrollSettings(),
      ]);
      setPeriods(next);
      setCompanyCurrencyCode(normaliseCurrencyCode(settings.currencyCode));
      const currentId = selectedPeriodIdRef.current;
      const nextSelection = currentId && next.some((period) => period.id === currentId)
        ? currentId
        : next[0]?.id ?? null;
      if (nextSelection !== currentId) {
        const selected = next.find((period) => period.id === nextSelection);
        selectPayPeriod(nextSelection, selected?.endDate);
      }
    } catch (err) {
      setError((err as Error).message || t('Failed to load pay periods'));
    } finally {
      setIsLoading(false);
    }
  }, [selectPayPeriod]);

  const loadTimesheets = useCallback(async (payPeriodId: string) => {
    const requestSequence = ++timesheetLoadSequence.current;
    setIsLoadingTimesheets(true);
    setLoadedTimesheetPeriodId(null);
    try {
      const [nextSheets, nextAggregate] = await Promise.all([
        timesheetService.listTimesheets(payPeriodId),
        timesheetService.fetchAggregateReport(payPeriodId, groupBy),
      ]);
      if (
        requestSequence !== timesheetLoadSequence.current
        || selectedPeriodIdRef.current !== payPeriodId
      ) return false;
      setTimesheets(nextSheets);
      setAggregate(nextAggregate);
      setLoadedTimesheetPeriodId(payPeriodId);
      if (expandedIdRef.current && !nextSheets.some((sheet) => sheet.id === expandedIdRef.current)) {
        clearExpandedDetail();
      }
      return true;
    } catch (err) {
      if (
        requestSequence !== timesheetLoadSequence.current
        || selectedPeriodIdRef.current !== payPeriodId
      ) return false;
      setTimesheets([]);
      setAggregate([]);
      setLoadedTimesheetPeriodId(null);
      setError((err as Error).message || t('Failed to load timesheets'));
      return false;
    } finally {
      if (
        requestSequence === timesheetLoadSequence.current
        && selectedPeriodIdRef.current === payPeriodId
      ) setIsLoadingTimesheets(false);
    }
  }, [clearExpandedDetail, groupBy]);

  useEffect(() => { void loadPeriods(); }, [loadPeriods]);

  useEffect(() => {
    if (selectedPeriodId) {
      void loadTimesheets(selectedPeriodId);
    } else {
      setLoadedTimesheetPeriodId(null);
      setTimesheets([]);
      setAggregate([]);
      setIsLoadingTimesheets(false);
    }
  }, [selectedPeriodId, loadTimesheets]);

  useEffect(() => {
    if (selectedPeriod && (entryDate < selectedPeriod.startDate || entryDate > selectedPeriod.endDate)) {
      setEntryDate(selectedPeriod.endDate);
    }
  }, [selectedPeriod, entryDate]);

  useEffect(() => {
    setSelectedPayDate(selectedPeriod?.payDate ?? selectedPeriod?.endDate ?? '');
  }, [selectedPeriod]);

  const loadTimesheetDetail = async (timesheetId: string, payPeriodId: string) => {
    const requestSequence = ++detailLoadSequence.current;
    expandedIdRef.current = timesheetId;
    setExpandedId(timesheetId);
    setRemovingEntry(null);
    setEntries([]);
    setAuditEvents([]);
    try {
      const [nextEntries, nextAudit] = await Promise.all([
        timesheetService.listEntries(timesheetId),
        timesheetService.listAuditEvents(timesheetId),
      ]);
      if (
        requestSequence !== detailLoadSequence.current
        || selectedPeriodIdRef.current !== payPeriodId
        || expandedIdRef.current !== timesheetId
      ) return;
      setEntries(nextEntries);
      setAuditEvents(nextAudit);
    } catch (err) {
      if (
        requestSequence !== detailLoadSequence.current
        || selectedPeriodIdRef.current !== payPeriodId
      ) return;
      setError((err as Error).message || t('Failed to load timesheet detail'));
    }
  };

  const openTimesheet = async (timesheet: Timesheet) => {
    let period: PayPeriod;
    try {
      ({ period } = requireLoadedTimesheet(timesheet.id));
    } catch (err) {
      setError((err as Error).message);
      return;
    }
    if (expandedIdRef.current === timesheet.id) {
      clearExpandedDetail();
      return;
    }
    setDrillTab('lines');
    await loadTimesheetDetail(timesheet.id, period.id);
  };

  interface ActionRefresh {
    clearDetail?: boolean;
    refreshTimesheetId?: string;
    refreshPeriodId?: string;
  }

  const runAction = async (
    action: () => Promise<ActionRefresh | void>,
    successMessage: string,
  ) => {
    setIsBusy(true);
    setError('');
    setMessage('');
    try {
      const refresh = await action();
      if (refresh?.clearDetail) clearExpandedDetail();
      setMessage(successMessage);
      await loadPeriods();
      const activePeriodId = selectedPeriodIdRef.current;
      const loaded = activePeriodId ? await loadTimesheets(activePeriodId) : false;
      if (
        loaded
        && refresh?.refreshTimesheetId
        && refresh.refreshPeriodId === selectedPeriodIdRef.current
        && expandedIdRef.current === refresh.refreshTimesheetId
      ) {
        await loadTimesheetDetail(refresh.refreshTimesheetId, refresh.refreshPeriodId);
      }
    } catch (err) {
      setError((err as Error).message || t('Something went wrong'));
    } finally {
      setIsBusy(false);
    }
  };

  const createPeriod = () => runAction(async () => {
    if (!periodLabel.trim()) throw new Error(t('Give the pay period a label.'));
    if (periodStart > periodEnd) throw new Error(t('The start date must be before the end date.'));
    if (!periodPayDate) throw new Error(t('Choose the employee pay date.'));
    const created = await timesheetService.createPayPeriod({
      label: periodLabel,
      frequency: 'custom',
      startDate: periodStart,
      endDate: periodEnd,
      payDate: periodPayDate,
    });
    selectPayPeriod(created.id, created.endDate);
    setPeriodLabel('');
    setShowPeriodForm(false);
    return { clearDetail: true };
  }, t('Pay period created.'));

  const generate = () => runAction(async () => {
    const period = requireLoadedPeriod();
    await timesheetService.generateTimesheets(period.id);
    return { clearDetail: true };
  }, t('Timesheets recalculated from shift records.'));

  const setSheetStatus = (
    timesheet: Timesheet,
    status: Timesheet['status'],
    options: { reason?: string; note?: string } = {},
  ) => runAction(
    async () => {
      const { period } = requireLoadedTimesheet(timesheet.id);
      await timesheetService.setTimesheetStatus(timesheet.id, status, options);
      return { refreshTimesheetId: timesheet.id, refreshPeriodId: period.id };
    },
    status === 'approved' ? t('Timesheet approved.') : t('Timesheet updated.'),
  );

  const setPeriodStatus = (status: PayPeriodStatus) => runAction(async () => {
    const period = requireLoadedPeriod();
    await timesheetService.setPayPeriodStatus(period.id, status);
    setShowLockConfirmation(false);
    setShowPaidConfirmation(false);
    return expandedIdRef.current
      ? { refreshTimesheetId: expandedIdRef.current, refreshPeriodId: period.id }
      : undefined;
  }, t('Pay period updated.'));

  const saveSelectedPayDate = () => runAction(async () => {
    const period = requireLoadedPeriod();
    if (!selectedPayDate) throw new Error(t('Choose the employee pay date.'));
    await timesheetService.updatePayPeriodPayDate(period.id, selectedPayDate);
  }, t('Pay date updated.'));

  const rejectTimesheet = () => {
    if (!rejectingTimesheet) return;
    const reason = rejectionReason.trim();
    if (!reason) {
      setError(t('Give a reason so the correction is clear in the audit trail.'));
      return;
    }
    const target = rejectingTimesheet;
    setRejectingTimesheet(null);
    setRejectionReason('');
    void setSheetStatus(target, 'rejected', { reason });
  };

  const requestTimesheetApproval = (timesheet: Timesheet) => {
    if (timesheet.flaggedCount <= 0) {
      void setSheetStatus(timesheet, 'approved');
      return;
    }
    setRejectingTimesheet(null);
    setRejectionReason('');
    setApprovingTimesheet(timesheet);
    setApprovalNote('');
  };

  const approveFlaggedTimesheet = () => {
    if (!approvingTimesheet) return;
    const note = approvalNote.trim();
    if (!note) {
      setError(t('Explain why the remaining exceptions are acceptable for this pay run.'));
      return;
    }
    const target = approvingTimesheet;
    setApprovingTimesheet(null);
    setApprovalNote('');
    void setSheetStatus(target, 'approved', { note });
  };

  const downloadLockedRegister = async () => {
    if (!selectedPeriod || !isPeriodLocked || isLoadingTimesheets || !hasLoadedSelectedPeriod) return;
    setIsBusy(true);
    setError('');
    setMessage('');
    try {
      const period = requireLoadedPeriod();
      const csv = buildTimesheetRegisterCsv(period, displayedTimesheets, selectedCurrencyCode);
      const filename = buildTimesheetRegisterFilename(period);
      await timesheetService.recordPeriodExport(period.id, `Locked register: ${filename}`);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setMessage(t('Locked timesheet register downloaded and recorded in the audit trail.'));
    } catch (err) {
      setError((err as Error).message || t('The register could not be exported or added to the audit trail.'));
    } finally {
      setIsBusy(false);
    }
  };

  const addEntry = (timesheetId: string) => runAction(async () => {
    const { period } = requireLoadedTimesheet(timesheetId);
    if (!entryDescription.trim()) throw new Error(t('Describe the entry.'));
    if (entryDate < period.startDate || entryDate > period.endDate) {
      throw new Error(t('Entry date must be inside the selected pay period.'));
    }
    const quantity = Number(entryQuantity);
    const unitRate = Number(entryRate);
    if (!Number.isFinite(quantity) || quantity <= 0) throw new Error(t('Enter a quantity greater than zero.'));
    if (!Number.isFinite(unitRate) || unitRate < 0) throw new Error(t('Enter a rate of zero or more.'));
    await timesheetService.addManualEntry({
      timesheetId,
      entryType,
      workDate: entryDate,
      description: entryDescription,
      quantity,
      unitRate,
    });
    setEntryDescription('');
    return { refreshTimesheetId: timesheetId, refreshPeriodId: period.id };
  }, t('Entry added.'));

  const removeEntry = (timesheetId: string) => runAction(async () => {
    const { period } = requireLoadedTimesheet(timesheetId);
    if (!removingEntry) return;
    await timesheetService.deleteEntry(removingEntry.id, timesheetId);
    setRemovingEntry(null);
    return { refreshTimesheetId: timesheetId, refreshPeriodId: period.id };
  }, t('Entry removed.'));

  const periodTotals = useMemo(() => displayedTimesheets.reduce((acc, sheet) => ({
    gross: acc.gross + sheet.grossPay,
    payable: acc.payable + sheet.grossPay + sheet.expensePay + sheet.deductionPay,
    ordinary: acc.ordinary + sheet.ordinaryMinutes,
    overtime: acc.overtime + sheet.overtimeMinutes + sheet.doubleTimeMinutes,
    flagged: acc.flagged + sheet.flaggedCount,
    approved: acc.approved + (sheet.status === 'approved' || sheet.status === 'locked' ? 1 : 0),
  }), {
    gross: 0,
    payable: 0,
    ordinary: 0,
    overtime: 0,
    flagged: 0,
    approved: 0,
  }), [displayedTimesheets]);

  const allTimesheetsApproved = displayedTimesheets.length > 0
    && displayedTimesheets.every((sheet) => sheet.status === 'approved' || sheet.status === 'locked');
  const pendingTimesheetCount = displayedTimesheets
    .filter((sheet) => sheet.status !== 'approved' && sheet.status !== 'locked').length;

  // Totals stay whole-period; only the review table narrows.
  const visibleTimesheets = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return displayedTimesheets.filter((sheet) => {
      if (term && !sheet.guardName.toLowerCase().includes(term)) return false;
      switch (reviewFilter) {
        case 'pending':
          return sheet.status !== 'approved' && sheet.status !== 'locked';
        case 'flagged':
          return sheet.flaggedCount > 0;
        case 'approved':
          return sheet.status === 'approved' || sheet.status === 'locked';
        default:
          return true;
      }
    });
  }, [displayedTimesheets, reviewFilter, searchTerm]);


  return (
    <div className="pay-page">
      <div className="pay-page__header" data-tour="payroll-timesheet-header">
        <div className="pay-page__heading">
          <h1 className="pay-page__title">{t('Timesheets')}</h1>
          <p className="pay-page__subtitle">
            {t('Prepare reviewed gross-pay figures for your payroll bureau. Tax, PRSI, USC, net pay and payslips remain in your payroll provider.')}
          </p>
        </div>
        <div className="pay-page__actions">
          <button type="button" className="btn btn--secondary" onClick={() => setShowPeriodForm((value) => !value)}>
            <FiPlus aria-hidden="true" />
            {t('New period')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={generate}
            disabled={isBusy || !selectedPeriodId || !canEditPeriod || !hasLoadedSelectedPeriod}
            data-tour="payroll-timesheet-recalculate"
          >
            <FiRefreshCw aria-hidden="true" />
            {isBusy ? t('Working...') : t('Recalculate')}
          </button>
        </div>
      </div>

      {(error || message) && (
        <div className="admin-account-page__messages">
          {error && <div className="msg--error">{error}</div>}
          {message && <div className="msg--success">{message}</div>}
        </div>
      )}

      {showPeriodForm && (
        <section className="pay-panel">
          <div className="pay-panel__head">
            <div><h2>{t('New pay period')}</h2></div>
          </div>
          <div className="pay-form-row">
            <label className="form__group">
              <span className="form__label">{t('Label')}</span>
              <input
                className="form__input"
                value={periodLabel}
                required
                placeholder={t('Week 12')}
                onChange={(event) => setPeriodLabel(event.target.value)}
              />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Start date')}</span>
              <input className="form__input" type="date" value={periodStart} required onChange={(event) => setPeriodStart(event.target.value)} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('End date')}</span>
              <input className="form__input" type="date" value={periodEnd} required onChange={(event) => setPeriodEnd(event.target.value)} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Pay date')}</span>
              <input className="form__input" type="date" value={periodPayDate} required onChange={(event) => setPeriodPayDate(event.target.value)} />
            </label>
            <button type="button" className="btn btn--primary" onClick={createPeriod} disabled={isBusy}>
              {t('Create')}
            </button>
          </div>
        </section>
      )}

      <div data-tour="payroll-timesheet-periods">
        {isLoading ? (
          <section className="pay-panel">
            <div className="pay-empty">{t('Loading pay periods...')}</div>
          </section>
        ) : periods.length === 0 ? (
          <section className="pay-panel">
            <div className="pay-empty">{t('No pay periods yet. Create one to start a payroll run.')}</div>
          </section>
        ) : (
          <section className="pay-panel pay-period-bar">
            <label className="form__group pay-period-bar__picker">
              <span className="form__label">{t('Pay period')}</span>
              <select
                className="form__input"
                value={selectedPeriodId ?? ''}
                disabled={isBusy}
                onChange={(event) => {
                  const next = periods.find((period) => period.id === event.target.value);
                  selectPayPeriod(next?.id ?? null, next?.endDate);
                }}
              >
                {periods.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.label} · {period.startDate} → {period.endDate}
                  </option>
                ))}
              </select>
            </label>

            {selectedPeriod && (
              <div className="pay-period-bar__facts" role="status">
                <div className="pay-period-fact">
                  <span>{t('Current stage')}</span>
                  <strong>
                    <span className={`pay-chip ${STATUS_CHIP[selectedPeriod.status] ?? 'pay-chip--muted'}`}>
                      {t(PERIOD_STATUS_LABELS[selectedPeriod.status] ?? selectedPeriod.status)}
                    </span>
                  </strong>
                </div>
                <div className="pay-period-fact">
                  <span>{t('Approval progress')}</span>
                  <strong>{periodTotals.approved}/{displayedTimesheets.length}</strong>
                </div>
                <div className="pay-period-fact">
                  <span>{t('Awaiting decision')}</span>
                  <strong>{pendingTimesheetCount}</strong>
                </div>
                <div className="pay-period-fact">
                  <span>{t('Pay date')}</span>
                  <strong>
                    {selectedPeriod.payDate ?? t('Not set')}
                    {canEditPeriod && (
                      <button
                        type="button"
                        className="pay-link-btn"
                        style={{ marginLeft: 8 }}
                        onClick={() => setIsEditingPayDate((value) => !value)}
                      >
                        <FiEdit2 aria-hidden="true" /> {t('Change')}
                      </button>
                    )}
                  </strong>
                </div>
              </div>
            )}

            {selectedPeriod && (
              <div className="pay-inline-actions" data-tour="payroll-period-actions">
                {selectedPeriod.status === 'open' && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => setPeriodStatus('in_review')}
                    disabled={isBusy || isLoadingTimesheets || !hasLoadedSelectedPeriod || displayedTimesheets.length === 0 || !selectedPeriod.payDate}
                    title={!selectedPeriod.payDate ? t('Save the employee pay date before starting review.') : undefined}
                  >
                    <FiCheck aria-hidden="true" />
                    {t('Start review')}
                  </button>
                )}
                {selectedPeriod.status === 'in_review' && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => setPeriodStatus('approved')}
                    disabled={isBusy || isLoadingTimesheets || !hasLoadedSelectedPeriod || !allTimesheetsApproved}
                    title={!allTimesheetsApproved ? t('Approve every employee timesheet first.') : undefined}
                  >
                    <FiCheck aria-hidden="true" />
                    {t('Approve period')}
                  </button>
                )}
                {selectedPeriod.status === 'approved' && canLockPeriod && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => setShowLockConfirmation(true)}
                    disabled={isBusy || isLoadingTimesheets || !hasLoadedSelectedPeriod || !allTimesheetsApproved}
                  >
                    <FiLock aria-hidden="true" />
                    {t('Lock for export')}
                  </button>
                )}
                {selectedPeriod.status === 'approved' && !canLockPeriod && (
                  <span className="pay-table__muted">{t('Approved — awaiting an admin lock')}</span>
                )}
                {isPeriodLocked && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => { void downloadLockedRegister(); }}
                    disabled={isBusy || isLoadingTimesheets || !hasLoadedSelectedPeriod || displayedTimesheets.length === 0}
                    data-tour="payroll-locked-export"
                  >
                    <FiDownload aria-hidden="true" />
                    {t('Download locked register')}
                  </button>
                )}
                {selectedPeriod.status === 'locked' && canLockPeriod && (
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setShowPaidConfirmation(true)}
                    disabled={isBusy || isLoadingTimesheets || !hasLoadedSelectedPeriod}
                  >
                    <FiCheck aria-hidden="true" />
                    {t('Mark paid')}
                  </button>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {selectedPeriod && canEditPeriod && (isEditingPayDate || !selectedPeriod.payDate) && (
        <section className="pay-panel">
          <div className="pay-form-row">
            <label className="form__group">
              <span className="form__label">{t('Employee pay date')}</span>
              <input
                className="form__input"
                type="date"
                value={selectedPayDate}
                required
                onChange={(event) => setSelectedPayDate(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => { saveSelectedPayDate(); setIsEditingPayDate(false); }}
              disabled={isBusy || !hasLoadedSelectedPeriod || !selectedPayDate || selectedPayDate === (selectedPeriod.payDate ?? '')}
            >
              {t('Save pay date')}
            </button>
            <span className="pay-table__muted">
              {t('Revenue payroll reporting uses the date employees are paid.')}
            </span>
          </div>
        </section>
      )}

      {showLockConfirmation && selectedPeriod?.status === 'approved' && (
        <div className="pay-confirm" role="region" aria-labelledby="lock-period-title">
          <FiAlertTriangle aria-hidden="true" />
          <div className="pay-confirm__copy">
            <strong id="lock-period-title">{t('Lock this approved pay period?')}</strong>
            <span>{t('Locking freezes every timesheet so recalculation and adjustments cannot change the exported figures.')}</span>
          </div>
          <div className="pay-inline-actions">
            <button type="button" className="btn btn--secondary" onClick={() => setShowLockConfirmation(false)} disabled={isBusy}>
              {t('Cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setPeriodStatus('locked')} disabled={isBusy || !hasLoadedSelectedPeriod}>
              <FiLock aria-hidden="true" />
              {isBusy ? t('Working...') : t('Lock period')}
            </button>
          </div>
        </div>
      )}

      {showPaidConfirmation && selectedPeriod?.status === 'locked' && (
        <div className="pay-confirm" role="region" aria-labelledby="paid-period-title">
          <FiAlertTriangle aria-hidden="true" />
          <div className="pay-confirm__copy">
            <strong id="paid-period-title">{t('Mark this pay period as paid?')}</strong>
            <span>{t('Only do this after your payroll provider has completed the run. Paid periods remain permanently locked.')}</span>
          </div>
          <div className="pay-inline-actions">
            <button type="button" className="btn btn--secondary" onClick={() => setShowPaidConfirmation(false)} disabled={isBusy}>
              {t('Cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setPeriodStatus('paid')} disabled={isBusy || !hasLoadedSelectedPeriod}>
              <FiCheck aria-hidden="true" />
              {isBusy ? t('Working...') : t('Confirm paid')}
            </button>
          </div>
        </div>
      )}

      {selectedPeriod && (
        <>
          {/* Money first, then the exception count, then the hour counts that
              only matter once a figure looks wrong. Approval progress lives in
              the period bar above and is not repeated here. */}
          <div className="pay-stats">
            <div className="pay-stat pay-stat--money pay-stat--lead">
              <span>{t('Amount payable')}</span>
              <strong>{formatMoney(periodTotals.payable, selectedCurrencyCode)}</strong>
            </div>
            <div className="pay-stat pay-stat--money">
              <span>{t(isPeriodLocked ? 'Locked gross pay' : 'Estimated gross pay')}</span>
              <strong>{formatMoney(periodTotals.gross, selectedCurrencyCode)}</strong>
            </div>
            <div className="pay-stat pay-stat--warn">
              <span>{t('Lines to review')}</span>
              <strong>{periodTotals.flagged}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Ordinary hours')}</span>
              <strong>{formatHours(periodTotals.ordinary)}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Overtime hours')}</span>
              <strong>{formatHours(periodTotals.overtime)}</strong>
            </div>
          </div>

          <section className="pay-panel">
            <div className="pay-panel__head" data-tour="payroll-timesheet-review">
              <div style={{ flex: 1 }}>
                <h2>{t('Employee review')}</h2>
                <p>{t('Open a row to see its pay lines, add adjustments and view the audit trail.')}</p>
              </div>
            </div>

            {displayedTimesheets.length > 0 && (
              <div className="pay-filters">
                <label className="form__group pay-filters__search">
                  <span className="form__label pay-sr-only">{t('Search employees')}</span>
                  <input
                    className="form__input"
                    type="search"
                    value={searchTerm}
                    placeholder={t('Search employees')}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </label>
                <div className="pay-segment">
                  {REVIEW_FILTERS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`pay-segment__item ${reviewFilter === option.key ? 'pay-segment__item--active' : ''}`}
                      onClick={() => setReviewFilter(option.key)}
                    >
                      {t(option.label)}
                    </button>
                  ))}
                </div>
                <span className="pay-filters__count">
                  {t('{shown} of {total} employees', {
                    shown: visibleTimesheets.length,
                    total: displayedTimesheets.length,
                  })}
                </span>
              </div>
            )}

            {approvingTimesheet && (
              <div className="pay-confirm" role="region" aria-labelledby="approve-timesheet-title">
                <FiAlertTriangle aria-hidden="true" />
                <div className="pay-confirm__copy">
                  <strong id="approve-timesheet-title">
                    {t('Approve {employee} with exceptions?', { employee: approvingTimesheet.guardName })}
                  </strong>
                  <span>
                    {t('{count} flagged pay line(s) remain. Record why they are acceptable; the note will be retained in the audit trail.', {
                      count: approvingTimesheet.flaggedCount,
                    })}
                  </span>
                  <label className="form__group">
                    <span className="form__label">{t('Approval note')}</span>
                    <textarea
                      className="form__input"
                      rows={2}
                      value={approvalNote}
                      onChange={(event) => setApprovalNote(event.target.value)}
                      placeholder={t('Explain why the exceptions are acceptable for this run')}
                    />
                  </label>
                </div>
                <div className="pay-inline-actions">
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => { setApprovingTimesheet(null); setApprovalNote(''); }}
                    disabled={isBusy}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={approveFlaggedTimesheet}
                    disabled={isBusy || !approvalNote.trim()}
                  >
                    <FiCheck aria-hidden="true" />
                    {t('Approve with note')}
                  </button>
                </div>
              </div>
            )}

            {rejectingTimesheet && (
              <div className="pay-confirm pay-confirm--danger" role="region" aria-labelledby="reject-timesheet-title">
                <FiAlertTriangle aria-hidden="true" />
                <div className="pay-confirm__copy">
                  <strong id="reject-timesheet-title">
                    {t('Return {employee} for correction', { employee: rejectingTimesheet.guardName })}
                  </strong>
                  <label className="form__group">
                    <span className="form__label">{t('Reason')}</span>
                    <textarea
                      className="form__input"
                      rows={2}
                      value={rejectionReason}
                      onChange={(event) => setRejectionReason(event.target.value)}
                      placeholder={t('Explain what needs to be corrected')}
                    />
                  </label>
                </div>
                <div className="pay-inline-actions">
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => { setRejectingTimesheet(null); setRejectionReason(''); }}
                    disabled={isBusy}
                  >
                    {t('Cancel')}
                  </button>
                  <button type="button" className="btn btn--primary" onClick={rejectTimesheet} disabled={isBusy || !rejectionReason.trim()}>
                    <FiX aria-hidden="true" />
                    {t('Return for correction')}
                  </button>
                </div>
              </div>
            )}

            {isLoadingTimesheets ? (
              <div className="pay-empty">{t('Loading timesheets...')}</div>
            ) : displayedTimesheets.length === 0 ? (
              <div className="pay-empty">{t('No timesheets yet. Use Recalculate to build them from shift records.')}</div>
            ) : visibleTimesheets.length === 0 ? (
              <div className="pay-empty">
                {t('No employees match these filters.')}{' '}
                <button
                  type="button"
                  className="pay-link-btn"
                  onClick={() => { setReviewFilter('all'); setSearchTerm(''); }}
                >
                  {t('Clear filters')}
                </button>
              </div>
            ) : (
              <div className="pay-table-wrap">
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>{t('Employee')}</th>
                      <th>{t('Band')}</th>
                      <th>{t('Status')}</th>
                      <th className="pay-table__num">{t('Shifts')}</th>
                      <th className="pay-table__num">{t('Ordinary')}</th>
                      <th className="pay-table__num">{t('Overtime')}</th>
                      <th className="pay-table__num">{t('Unsocial')}</th>
                      <th className="pay-table__money">{t('Gross')}</th>
                      <th className="pay-table__money">{t('Amount payable')}</th>
                      <th>{t('Review')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTimesheets.map((sheet) => (
                      <React.Fragment key={sheet.id}>
                        <tr>
                          <td>
                            <button type="button" className="pay-table__row-button" onClick={() => { void openTimesheet(sheet); }}>
                              {expandedId === sheet.id ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
                              {sheet.guardName}
                            </button>
                          </td>
                          <td><span className="pay-chip pay-chip--band">{sheet.payBandCode ?? '-'}</span></td>
                          <td><span className={`pay-chip ${STATUS_CHIP[sheet.status] ?? 'pay-chip--muted'}`}>{t(TIMESHEET_STATUS_LABELS[sheet.status] ?? sheet.status)}</span></td>
                          <td className="pay-table__num">{sheet.shiftCount}</td>
                          <td className="pay-table__num">{formatHours(sheet.ordinaryMinutes)}</td>
                          <td className="pay-table__num">
                            {formatHours(sheet.overtimeMinutes + sheet.doubleTimeMinutes)}
                            {sheet.overtimeMinutes + sheet.doubleTimeMinutes > 0 && (
                              <span className="pay-chip pay-chip--ot" style={{ marginLeft: 6 }}>{t('OT')}</span>
                            )}
                          </td>
                          <td className="pay-table__num">{formatHours(sheet.unsocialMinutes)}</td>
                          <td className="pay-table__money">{formatMoney(sheet.grossPay, selectedCurrencyCode)}</td>
                          <td className="pay-table__money">
                            {formatMoney(
                              sheet.grossPay + sheet.expensePay + sheet.deductionPay,
                              selectedCurrencyCode,
                            )}
                          </td>
                          <td className="pay-table__num">{sheet.flaggedCount || '-'}</td>
                          <td>
                            {selectedPeriod.status === 'in_review' && sheet.status !== 'locked' && (
                              <div className="pay-inline-actions">
                                <button
                                  type="button"
                                  className="pay-link-btn"
                                  onClick={() => requestTimesheetApproval(sheet)}
                                  disabled={isBusy || sheet.status === 'approved'}
                                >
                                  <FiCheck aria-hidden="true" /> {t('Approve')}
                                </button>
                                <button
                                  type="button"
                                  className="pay-link-btn pay-link-btn--danger"
                                  onClick={() => {
                                    setApprovingTimesheet(null);
                                    setApprovalNote('');
                                    setRejectingTimesheet(sheet);
                                    setRejectionReason('');
                                  }}
                                  disabled={isBusy || sheet.status === 'rejected'}
                                >
                                  <FiX aria-hidden="true" /> {t('Reject')}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>

                        {expandedId === sheet.id && (
                          <tr className="pay-drill">
                            <td colSpan={11}>
                              <div className="pay-drill__inner">
                                {/* One slice at a time: stacking pay lines,
                                    adjustments and the audit trail buried the
                                    row the reviewer opened. */}
                                <div className="pay-drill__toolbar">
                                  <span className="pay-drill__subject">{sheet.guardName}</span>
                                  <div className="pay-segment">
                                    <button
                                      type="button"
                                      className={`pay-segment__item ${drillTab === 'lines' ? 'pay-segment__item--active' : ''}`}
                                      onClick={() => setDrillTab('lines')}
                                    >
                                      {t('Pay lines')}
                                    </button>
                                    {canEditPeriod && sheet.status !== 'approved' && sheet.status !== 'locked' && (
                                      <button
                                        type="button"
                                        className={`pay-segment__item ${drillTab === 'adjustments' ? 'pay-segment__item--active' : ''}`}
                                        onClick={() => setDrillTab('adjustments')}
                                      >
                                        {t('Add non-shift pay')}
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className={`pay-segment__item ${drillTab === 'audit' ? 'pay-segment__item--active' : ''}`}
                                      onClick={() => setDrillTab('audit')}
                                    >
                                      {t('Audit trail')}
                                    </button>
                                  </div>
                                </div>

                                {drillTab === 'lines' && (
                                <div className="pay-table-wrap">
                                  <table className="pay-table">
                                    <thead>
                                      <tr>
                                        <th>{t('Date')}</th>
                                        <th>{t('Type')}</th>
                                        <th>{t('Description')}</th>
                                        <th>{t('Clocked')}</th>
                                        <th>{t('Paid window')}</th>
                                        <th className="pay-table__money">{t('Rate')}</th>
                                        <th className="pay-table__num">{t('Ordinary hours')}</th>
                                        <th className="pay-table__num">{t('Overtime hours')}</th>
                                        <th className="pay-table__money">{t('Ordinary pay')}</th>
                                        <th className="pay-table__money">{t('Overtime pay')}</th>
                                        <th className="pay-table__money">{t('Premium pay')}</th>
                                        <th className="pay-table__num">{t('Rounding')}</th>
                                        <th className="pay-table__money">{t('Total')}</th>
                                        <th>{t('Flags')}</th>
                                        <th>{t('Actions')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {entries.length === 0 ? (
                                        <tr><td colSpan={15} className="pay-empty">{t('No pay lines.')}</td></tr>
                                      ) : entries.map((entry) => (
                                        <tr key={entry.id}>
                                          <td>{entry.workDate}</td>
                                          <td>
                                            <span className={`pay-chip ${entry.isManual ? 'pay-chip--warn' : 'pay-chip--muted'}`}>
                                              {t(ENTRY_TYPE_LABELS[entry.entryType] ?? entry.entryType)}
                                            </span>
                                          </td>
                                          <td className="pay-table__muted">{entry.description ?? '-'}</td>
                                          <td className="pay-table__muted" style={{ whiteSpace: 'nowrap' }}>
                                            {formatTimeWindow(entry.sourceStartAt, entry.sourceEndAt, entry.workTimeZone)}
                                          </td>
                                          <td className="pay-table__muted" style={{ whiteSpace: 'nowrap' }}>
                                            {formatTimeWindow(entry.effectiveStartAt, entry.effectiveEndAt, entry.workTimeZone)}
                                          </td>
                                          <td className="pay-table__money">
                                            {entry.isManual
                                              ? (entry.unitRate === null ? '-' : formatMoney(entry.unitRate, selectedCurrencyCode))
                                              : (entry.rateSnapshot === null ? '-' : formatMoney(entry.rateSnapshot, selectedCurrencyCode))}
                                          </td>
                                          <td className="pay-table__num">{formatHours(entry.ordinaryMinutes)}</td>
                                          <td className="pay-table__num">{formatHours(entry.overtimeMinutes + entry.doubleTimeMinutes)}</td>
                                          <td className="pay-table__money">{formatMoney(entry.ordinaryPay, selectedCurrencyCode)}</td>
                                          <td className="pay-table__money">{formatMoney(entry.overtimePay, selectedCurrencyCode)}</td>
                                          <td className="pay-table__money">{formatMoney(entry.premiumPay, selectedCurrencyCode)}</td>
                                          <td className="pay-table__num">
                                            {entry.roundingAdjustmentMinutes === 0
                                              ? '-'
                                              : `${entry.roundingAdjustmentMinutes > 0 ? '+' : ''}${entry.roundingAdjustmentMinutes}m`}
                                          </td>
                                          <td className="pay-table__money">{formatMoney(entry.totalPay, selectedCurrencyCode)}</td>
                                          <td>
                                            <div className="pay-flags">
                                              {entry.flags.map((flag) => (
                                                <span key={flag} className="pay-chip pay-chip--warn">{t(flag)}</span>
                                              ))}
                                            </div>
                                          </td>
                                          <td>
                                            {entry.isManual && canEditPeriod && sheet.status !== 'approved' && sheet.status !== 'locked' && (
                                              <button
                                                type="button"
                                                className="pay-link-btn pay-link-btn--danger"
                                                onClick={() => setRemovingEntry(entry)}
                                                disabled={isBusy}
                                              >
                                                <FiTrash2 aria-hidden="true" /> {t('Remove')}
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                )}

                                {drillTab === 'lines' && removingEntry && (
                                  <div className="pay-confirm pay-confirm--danger" role="region" aria-labelledby="remove-entry-title">
                                    <FiAlertTriangle aria-hidden="true" />
                                    <div className="pay-confirm__copy">
                                      <strong id="remove-entry-title">{t('Remove this manual pay entry?')}</strong>
                                      <span>{t('The audit trail will retain who removed it, its description and its original value.')}</span>
                                    </div>
                                    <div className="pay-inline-actions">
                                      <button type="button" className="btn btn--secondary" onClick={() => setRemovingEntry(null)} disabled={isBusy}>
                                        {t('Cancel')}
                                      </button>
                                      <button type="button" className="btn btn--primary" onClick={() => removeEntry(sheet.id)} disabled={isBusy}>
                                        <FiTrash2 aria-hidden="true" />
                                        {isBusy ? t('Working...') : t('Remove entry')}
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {drillTab === 'adjustments' && canEditPeriod && sheet.status !== 'approved' && sheet.status !== 'locked' && (
                                  <>
                                    <div className="pay-form-row">
                                      <label className="form__group">
                                        <span className="form__label">{t('Type')}</span>
                                        <select
                                          className="form__input"
                                          value={entryType}
                                          onChange={(event) => setEntryType(event.target.value as TimesheetEntryType)}
                                        >
                                          {MANUAL_ENTRY_TYPES.map((type) => (
                                            <option key={type} value={type}>{t(ENTRY_TYPE_LABELS[type])}</option>
                                          ))}
                                        </select>
                                      </label>
                                      <label className="form__group">
                                        <span className="form__label">{t('Date')}</span>
                                        <input
                                          className="form__input"
                                          type="date"
                                          min={selectedPeriod.startDate}
                                          max={selectedPeriod.endDate}
                                          value={entryDate}
                                          onChange={(event) => setEntryDate(event.target.value)}
                                        />
                                      </label>
                                      <label className="form__group">
                                        <span className="form__label">{t('Description')}</span>
                                        <input className="form__input" value={entryDescription} onChange={(event) => setEntryDescription(event.target.value)} />
                                      </label>
                                      <label className="form__group">
                                        <span className="form__label">{isTimeBased(entryType) ? t('Hours') : t('Units')}</span>
                                        <input className="form__input" type="number" min="0.01" step="0.25" value={entryQuantity} onChange={(event) => setEntryQuantity(event.target.value)} />
                                      </label>
                                      <label className="form__group">
                                        <span className="form__label">{isTimeBased(entryType) ? t('Hourly rate') : t('Unit amount')}</span>
                                        <input className="form__input" type="number" min="0" step="0.01" value={entryRate} onChange={(event) => setEntryRate(event.target.value)} />
                                      </label>
                                      <button type="button" className="btn btn--secondary" onClick={() => addEntry(sheet.id)} disabled={isBusy}>
                                        {t('Add entry')}
                                      </button>
                                    </div>
                                  </>
                                )}

                                {drillTab === 'audit' && (
                                <div className="pay-audit">
                                  {auditEvents.length === 0 ? (
                                    <span className="pay-table__muted">{t('No changes recorded yet.')}</span>
                                  ) : auditEvents.map((event) => (
                                    <div key={event.id} className="pay-audit__item">
                                      <span className="pay-audit__time">{formatTimestamp(event.occurredAt)}</span>
                                      <span>
                                        <strong>{event.actorName ?? t('System')}</strong>
                                        {' — '}
                                        {t(event.eventType)}
                                        {event.summary ? ` · ${event.summary}` : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <PayCollapsible
            title={t('Aggregate report')}
            description={t('Current timesheet figures rolled up across the selected period.')}
            meta={tn('{count} group', '{count} groups', displayedAggregate.length)}
            storageKey="odinseye.payroll.aggregateOpen"
          >
            <div className="pay-segment" style={{ marginBottom: 12 }}>
              {GROUP_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`pay-segment__item ${groupBy === option.key ? 'pay-segment__item--active' : ''}`}
                  onClick={() => setGroupBy(option.key)}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>

            {displayedAggregate.length === 0 ? (
              <div className="pay-empty">{t('Nothing to aggregate yet.')}</div>
            ) : (
              <div className="pay-table-wrap">
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>{t('Group')}</th>
                      <th className="pay-table__num">{t('Employees')}</th>
                      <th className="pay-table__num">{t('Shifts')}</th>
                      <th className="pay-table__num">{t('Ordinary')}</th>
                      <th className="pay-table__num">{t('Overtime')}</th>
                      <th className="pay-table__money">{t('Ordinary pay')}</th>
                      <th className="pay-table__money">{t('Premiums')}</th>
                      <th className="pay-table__money">{t('Total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedAggregate.map((row) => (
                      <tr key={row.groupKey}>
                        <td>{row.groupLabel}</td>
                        <td className="pay-table__num">{row.employeeCount}</td>
                        <td className="pay-table__num">{row.shiftCount}</td>
                        <td className="pay-table__num">{row.ordinaryHours.toFixed(2)}</td>
                        <td className="pay-table__num">{(row.overtimeHours + row.doubleTimeHours).toFixed(2)}</td>
                        <td className="pay-table__money">{formatMoney(row.ordinaryPay, selectedCurrencyCode)}</td>
                        <td className="pay-table__money">{formatMoney(row.premiumPay, selectedCurrencyCode)}</td>
                        <td className="pay-table__money">{formatMoney(row.totalPay, selectedCurrencyCode)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PayCollapsible>
        </>
      )}

      {periods.length > 0 && (
        <PayCollapsible
          title={t('All pay periods')}
          description={t('Every period this company has run, with its approval trail.')}
          meta={tn('{count} period', '{count} periods', periods.length)}
          storageKey="odinseye.payroll.periodArchiveOpen"
        >
          <div className="pay-table-wrap">
            <table className="pay-table">
              <thead>
                <tr>
                  <th>{t('Period')}</th>
                  <th>{t('Dates')}</th>
                  <th>{t('Pay date')}</th>
                  <th>{t('Status')}</th>
                  <th className="pay-table__num">{t('Timesheets')}</th>
                  <th className="pay-table__num">{t('Approved')}</th>
                  <th>{t('Approved by')}</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((period) => (
                  <tr key={period.id} style={{ background: period.id === selectedPeriodId ? 'rgba(255,255,255,0.04)' : undefined }}>
                    <td>
                      <button
                        type="button"
                        className="pay-table__row-button"
                        onClick={() => selectPayPeriod(period.id, period.endDate)}
                        disabled={isBusy}
                      >
                        {period.label}
                      </button>
                    </td>
                    <td className="pay-table__muted">{period.startDate} → {period.endDate}</td>
                    <td className="pay-table__muted">{period.payDate ?? '-'}</td>
                    <td><span className={`pay-chip ${STATUS_CHIP[period.status] ?? 'pay-chip--muted'}`}>{t(PERIOD_STATUS_LABELS[period.status] ?? period.status)}</span></td>
                    <td className="pay-table__num">{period.timesheetCount}</td>
                    <td className="pay-table__num">{period.approvedCount}</td>
                    <td className="pay-table__muted">{period.approvedByName ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PayCollapsible>
      )}
    </div>
  );
}
